import { useEffect, useRef, useState } from 'react'
import { Linking, Text, View } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { CameraView, useCameraPermissions, type CameraCapturedPicture } from 'expo-camera'
import * as Location from 'expo-location'
import { persistInspectionPhoto } from '@/lib/utils/inspection-photo-storage'
import { toast } from '@/lib/toast'
import { captureError } from '@/lib/utils/observability'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { Camera, Check, X } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import type { InspectionPhoto, InspectionPhotoArea } from '@/lib/types/inspection.types'

interface InspectionLiveCameraProps {
  visible: boolean
  area: InspectionPhotoArea | null
  areaLabel: string
  onCancel: () => void
  onCapture: (photo: InspectionPhoto) => void
}

export function InspectionLiveCamera({
  visible,
  area,
  areaLabel,
  onCancel,
  onCapture,
}: InspectionLiveCameraProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const cameraRef = useRef<CameraView>(null)
  const [permission, requestPermission] = useCameraPermissions()
  const [isReady, setIsReady] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)

  /*
   * Coordinates corroborate an inspection; they must never interrupt it.
   *
   * The handover camera is a hard, time-sensitive workflow. Asking for a new
   * location permission or a fresh GPS fix here surfaced Android's accuracy
   * dialog for every photo session, including after the person had explicitly
   * declined it. That turns optional metadata into a blocker.
   *
   * Read a recent OS-cached coordinate only when foreground permission already
   * exists. This path does not request permission, does not wake a provider,
   * and returns null when no trustworthy cached point is available. The live
   * photo, timestamp and digest remain the required evidence.
   *
   * A ref, not state: the preview must not re-render when metadata lands.
   */
  const locationRef = useRef<Location.LocationObject | null>(null)
  const locationPromiseRef = useRef<Promise<Location.LocationObject | null> | null>(null)

  useEffect(() => {
    if (!visible) return
    setIsReady(false)
    setIsCapturing(false)
    locationRef.current = null

    let cancelled = false
    const pending = getOptionalCachedLocation()
    locationPromiseRef.current = pending
    void pending.then((location) => {
      if (!cancelled) locationRef.current = location
    })
    return () => {
      cancelled = true
    }
  }, [visible, area])

  const capture = async () => {
    if (!area || !cameraRef.current || isCapturing || !isReady) return

    setIsCapturing(true)
    try {
      const capturedAt = new Date().toISOString()
      // EXIF va ENCENDIDO (decisión 2026-09-06): el tiempo y el GPS quedan
      // embebidos en la propia imagen y se validan contra la fila en
      // submit-inspection-assurance — la evidencia se ata al momento y al lugar
      // por el archivo, no solo por la fila. Si Android no entrega GPS en EXIF,
      // la captura sigue siendo válida: nunca se solicita ubicación desde esta
      // pantalla para completar una foto.
      const picture = (await cameraRef.current.takePictureAsync({
        quality: 0.86,
        exif: true,
        skipProcessing: false,
      })) as CameraCapturedPicture | undefined

      if (!picture?.uri) {
        // Una captura que no devuelve nada dejaba el obturador parpadeando y la
        // cuadricula igual que antes: la persona cree que tomo la foto, sigue,
        // y descubre el hueco al final. Un fallo silencioso en la evidencia de
        // un traspaso no es aceptable.
        void notification(NotificationType.Error)
        toast.error(t('checkin.capture_failed'))
        return
      }

      // EXIF es evidencia, no compuerta: si la cámara no lo devolvió (permiso
      // denegado, plataforma sin soporte), la foto sigue valiendo con el resto
      // del rastro (capturedAt + SHA-256 + source + GPS de la app).
      let exif: InspectionPhoto['exif']
      try {
        const raw = (picture as { exif?: Record<string, unknown> }).exif
        const dateTimeOriginal =
          typeof raw?.DateTimeOriginal === 'string' ? raw.DateTimeOriginal : undefined
        const gpsLat = raw?.GPSLatitude as number | undefined
        const gpsLng = raw?.GPSLongitude as number | undefined
        exif = {
          ...(dateTimeOriginal ? { dateTimeOriginal } : {}),
          ...(typeof gpsLat === 'number' ? { latitude: gpsLat } : {}),
          ...(typeof gpsLng === 'number' ? { longitude: gpsLng } : {}),
        }
      } catch {
        exif = undefined
      }

      /*
       * This normally resolves while the preview warms up. The first shutter
       * gets at most one second for that already-safe cache read; no coordinate
       * is preferable to making someone wait at a vehicle handover.
       */
      const location =
        locationRef.current ??
        (await Promise.race([
          locationPromiseRef.current ?? Promise.resolve(null),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 1_000)),
        ]))

      onCapture({
        uri: persistInspectionPhoto(picture.uri, area),
        area,
        width: picture.width,
        height: picture.height,
        capturedAt,
        source: 'tago_live_camera',
        latitude: location?.coords.latitude,
        longitude: location?.coords.longitude,
        locationAccuracyMeters: location?.coords.accuracy ?? undefined,
        exif,
      })
    } catch (err) {
      // Sin esto, `takePictureAsync` lanzando (memoria, camara ocupada, permiso
      // revocado a mitad) subia por el arbol sin que nadie dijera nada.
      captureError(err, { screen: 'inspection-camera', action: 'capture', area })
      void notification(NotificationType.Error)
      toast.error(t('checkin.capture_failed'))
    } finally {
      setIsCapturing(false)
    }
  }

  const permissionGranted = permission?.granted === true

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onCancel}
      // Native camera preview consumes its own touches — see LiveCameraModal.
    >
      <View style={{ flex: 1, backgroundColor: '#050506' }}>
        {permissionGranted ? (
          <CameraView
            ref={cameraRef}
            facing="back"
            mode="picture"
            style={{ flex: 1 }}
            onCameraReady={() => setIsReady(true)}
          >
            <View
              style={{
                flex: 1,
                justifyContent: 'space-between',
                paddingHorizontal: SPACING.lg,
                paddingTop: insets.top + SPACING.md,
                paddingBottom: insets.bottom + SPACING.lg,
                backgroundColor: 'rgba(0,0,0,0.16)',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Pressable
                  onPress={onCancel}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.cancel')}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.44)',
                  }}
                >
                  <X size={22} color="#FFFFFF" strokeWidth={2} />
                </Pressable>
                <View
                  style={{
                    paddingVertical: SPACING.xs,
                    paddingHorizontal: SPACING.md,
                    borderRadius: RADIUS.full,
                    backgroundColor: 'rgba(0,0,0,0.48)',
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      color: '#FFFFFF',
                      fontSize: 14,
                    }}
                  >
                    {areaLabel}
                  </Text>
                </View>
              </View>

              <View
                pointerEvents="none"
                style={{
                  alignSelf: 'center',
                  width: '88%',
                  aspectRatio: 4 / 3,
                  borderWidth: 2,
                  borderColor: 'rgba(255,255,255,0.86)',
                  borderRadius: RADIUS.lg,
                }}
              />

              <View style={{ alignItems: 'center', gap: SPACING.md }}>
                <Pressable
                  onPress={() => void capture()}
                  accessibilityRole="button"
                  accessibilityLabel={t('checkin.capture_live_photo')}
                  disabled={!isReady || isCapturing}
                  style={{
                    width: 78,
                    height: 78,
                    borderRadius: 39,
                    borderWidth: 4,
                    borderColor: '#FFFFFF',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: !isReady || isCapturing ? 0.55 : 1,
                  }}
                >
                  <View
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 29,
                      backgroundColor: COLORS.coral.dark,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isCapturing ? (
                      <Camera size={24} color="#FFFFFF" strokeWidth={2} />
                    ) : (
                      <Check size={28} color="#FFFFFF" strokeWidth={2.4} />
                    )}
                  </View>
                </Pressable>
              </View>
            </View>
          </CameraView>
        ) : (
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING.xl,
              gap: SPACING.lg,
              backgroundColor: colors.background,
            }}
          >
            <Camera size={44} color={colors.textSecondary} strokeWidth={1.6} />
            <Text
              style={{
                textAlign: 'center',
                fontFamily: FONTS.bodyBold,
                fontSize: 18,
                color: colors.textPrimary,
              }}
            >
              {t('checkin.camera_permission_title')}
            </Text>
            {/* "No volver a preguntar" era un callejon sin salida ENTERO, y en
                el peor momento posible: el anfitrion y el huesped parados junto
                al carro. En Android con canAskAgain === false,
                requestPermission() resuelve al instante como denegado y vuelve
                a pintar esta misma pantalla; el boton parecia funcionar y no
                hacia nada. Sin galeria de respaldo --la evidencia tiene que ser
                en vivo-- no se podia tomar una sola foto, nunca.

                El arreglo YA EXISTIA en el componente gemelo
                (components/camera/LiveCameraModal.tsx), escrito para este mismo
                caso. Que dos camaras hermanas trataran el mismo callejon de
                forma distinta es el defecto real; ahora las dos mandan a los
                ajustes del sistema, que es el unico lugar donde la decision se
                puede revertir. */}
            <Button
              title={
                permission?.canAskAgain === false
                  ? t('listing.camera_permission_settings_cta')
                  : t('checkin.camera_permission_cta')
              }
              onPress={() => {
                if (permission?.canAskAgain === false) {
                  void Linking.openSettings()
                  return
                }
                void requestPermission()
              }}
              testID="inspection-camera.permission"
            />
            <Button
              title={t('common.cancel')}
              variant="secondary"
              onPress={onCancel}
              testID="inspection-camera.cancel"
            />
          </View>
        )}
      </View>
    </AppModal>
  )
}

async function getOptionalCachedLocation(): Promise<Location.LocationObject | null> {
  try {
    const permission = await Location.getForegroundPermissionsAsync()
    if (!permission.granted) return null
    return await Location.getLastKnownPositionAsync({
      maxAge: 2 * 60 * 1_000,
      requiredAccuracy: 100,
    })
  } catch {
    return null
  }
}
